import { addWeeks, isAfter } from "date-fns";
import { z } from "zod";

const PROMPT_STATE_STORAGE_KEY_PREFIX = "passkeySetupPromptState_";
const PENDING_PROMPT_STORAGE_KEY = "pendingPasskeySetupPromptUserId";
const PROMPT_COOLDOWN_WEEKS = 4;

const promptStateSchema = z.object({
  lastPromptedAt: z.number().optional(),
  lastPasskeyLoginAt: z.number().optional(),
});

type PromptState = z.infer<typeof promptStateSchema>;

function getPromptStateStorageKey(userId: string): string {
  return `${PROMPT_STATE_STORAGE_KEY_PREFIX}${userId}`;
}

function getPromptState(userId: string): PromptState {
  try {
    const storedState = localStorage.getItem(getPromptStateStorageKey(userId));
    return storedState ? promptStateSchema.parse(JSON.parse(storedState)) : {};
  } catch {
    return {};
  }
}

function setPromptState(userId: string, state: PromptState): void {
  try {
    localStorage.setItem(getPromptStateStorageKey(userId), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable.
  }
}

function isWithinCooldown(timestamp: number | undefined, now: Date): boolean {
  return (
    timestamp !== undefined && isAfter(addWeeks(new Date(timestamp), PROMPT_COOLDOWN_WEEKS), now)
  );
}

export function requestPasskeySetupPrompt(userId: string): void {
  try {
    sessionStorage.setItem(PENDING_PROMPT_STORAGE_KEY, userId);
  } catch {
    // sessionStorage may be unavailable.
  }
}

export function hasPendingPasskeySetupPrompt(userId: string): boolean {
  try {
    return sessionStorage.getItem(PENDING_PROMPT_STORAGE_KEY) === userId;
  } catch {
    return false;
  }
}

export function clearPendingPasskeySetupPrompt(): void {
  try {
    sessionStorage.removeItem(PENDING_PROMPT_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable.
  }
}

export function shouldShowPasskeySetupPrompt(userId: string, now = new Date()): boolean {
  const state = getPromptState(userId);
  return (
    !isWithinCooldown(state.lastPromptedAt, now) && !isWithinCooldown(state.lastPasskeyLoginAt, now)
  );
}

export function recordPasskeySetupPromptShown(userId: string, now = new Date()): void {
  setPromptState(userId, { ...getPromptState(userId), lastPromptedAt: now.getTime() });
  clearPendingPasskeySetupPrompt();
}

export function recordPasskeyLogin(userId: string, now = new Date()): void {
  setPromptState(userId, { ...getPromptState(userId), lastPasskeyLoginAt: now.getTime() });
  clearPendingPasskeySetupPrompt();
}
